import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SignOut, Tray, ChatCircleText, FileText, Users, SquaresFour, Gear, Stack } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { SubmissionsPanel } from "./SubmissionsPanel";
import { ContentPanel } from "./ContentPanel";
import { SettingsPanel } from "./SettingsPanel";

type Tab = "overview" | "contact" | "quotes" | "applications" | "content" | "settings";

const tabs: { id: Tab; label: string; icon: any }[] = [
  { id: "overview", label: "Overview", icon: SquaresFour },
  { id: "contact", label: "Messages", icon: ChatCircleText },
  { id: "quotes", label: "Quote Requests", icon: FileText },
  { id: "applications", label: "Applications", icon: Users },
  { id: "content", label: "Site Content", icon: Stack },
  { id: "settings", label: "Settings", icon: Gear },
];

export default function AdminDashboard({ onLogout }: { onLogout: () => void }) {
  const [tab, setTab] = useState<Tab>("overview");
  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
  });

  const logout = () => {
    localStorage.removeItem("fosche_admin_token");
    onLogout();
  };

  const statCards = [
    { label: "Contact Messages", value: stats?.contact ?? "—", fresh: stats?.new_contact },
    { label: "Quote Requests", value: stats?.quotes ?? "—", fresh: stats?.new_quotes },
    { label: "Job Applications", value: stats?.applications ?? "—", fresh: stats?.new_applications },
    { label: "Projects", value: stats?.projects ?? "—" },
    { label: "Services", value: stats?.services ?? "—" },
    { label: "Open Roles", value: stats?.careers ?? "—" },
  ];

  return (
    <div className="min-h-screen bg-canvas flex" data-testid="admin-dashboard">
      <aside className="w-56 shrink-0 border-r border-line bg-panel min-h-screen hidden md:flex flex-col">
        <div className="p-5 border-b border-line">
          <Link to="/" className="flex items-center gap-2">
            <img src="/brand/logo-icon.png" alt="FOSCHE logo" className="h-8 w-auto" />
            <span className="flex items-baseline gap-1.5">
              <span className="font-logo text-sm text-ink">FOSCHE</span>
              <span className="font-mono-tech text-[9px] tracking-[0.25em] text-blueprint">ADMIN</span>
            </span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-sm transition-colors ${
                tab === t.id ? "bg-blueprint text-white" : "text-sub hover:text-ink hover:bg-canvas"
              }`}
            >
              <t.icon size={17} /> {t.label}
            </button>
          ))}
        </nav>
        <button
          data-testid="admin-logout-btn"
          onClick={logout}
          className="m-3 flex items-center gap-3 px-3.5 py-2.5 text-sm text-sub hover:text-red-500 transition-colors border border-line"
        >
          <SignOut size={17} /> Sign Out
        </button>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="md:hidden border-b border-line bg-panel p-3 flex gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-xs whitespace-nowrap ${tab === t.id ? "bg-blueprint text-white" : "text-sub"}`}
            >
              {t.label}
            </button>
          ))}
          <button onClick={logout} className="px-3 py-2 text-xs text-red-500">
            Sign Out
          </button>
        </div>

        <main className="p-6 md:p-10">
          {tab === "overview" && (
            <div>
              <p className="overline-label mb-2">Control Room</p>
              <h1 className="font-heading font-medium text-3xl tracking-tight text-ink mb-8">Dashboard</h1>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-testid="admin-stats-grid">
                {statCards.map((c) => (
                  <div key={c.label} className="border border-line bg-panel p-5">
                    <p className="font-mono-tech text-[10px] tracking-[0.18em] uppercase text-sub">{c.label}</p>
                    <div className="mt-2 flex items-end gap-2">
                      <span className="font-heading font-medium text-3xl tracking-tight text-ink">{c.value}</span>
                      {!!c.fresh && (
                        <span className="mb-1 font-mono-tech text-[10px] px-1.5 py-0.5 bg-[var(--gold)] text-black">
                          {c.fresh} new
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-sm text-sub max-w-lg">
                Use the sidebar to review inbound submissions, manage site content (projects, services, careers,
                testimonials), and update company settings shown across the website.
              </p>
            </div>
          )}
          {(tab === "contact" || tab === "quotes" || tab === "applications") && <SubmissionsPanel kind={tab} />}
          {tab === "content" && <ContentPanel />}
          {tab === "settings" && <SettingsPanel />}
        </main>
      </div>
    </div>
  );
}
